import { doc, onSnapshot } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { db } from "../firebase";
import "../styles/Portfolio.css";
import { useScrollReveal } from "../hooks/useScrollReveal";

const defaultProjects = [
  {
    id: 1,
    title: "Galaxy Strike",
    category: "Action Game",
    description: "Fast-paced space shooter with 80+ levels, boss fights, and global leaderboards. Built with Unity for buttery-smooth 60 fps on both platforms.",
    icon: "🚀",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    accent: "#e94560",
    platforms: ["iOS", "Android"],
    downloads: "2.4M+",
    rating: 4.8,
    tech: ["Unity 3D", "Firebase", "PlayFab"],
  },
  {
    id: 2,
    title: "Block Blitz",
    category: "Hyper-casual",
    description: "One-tap hyper-casual game with satisfying block-stacking mechanics. Reached #3 in the US Arcade chart within 2 weeks of launch.",
    icon: "🟦",
    gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
    accent: "#fff",
    platforms: ["iOS", "Android"],
    downloads: "5.1M+",
    rating: 4.6,
    tech: ["Unity", "AdMob", "MAX Mediation"],
  },
  {
    id: 3,
    title: "Second Marriage",
    category: "Social App",
    description: "Halal matrimonial app with AI profile matching, Wali (guardian) system, and Islamic-first UX. Live on App Store & Google Play.",
    icon: "💍",
    gradient: "linear-gradient(135deg, #f37498 0%, #fb897e 100%)",
    accent: "#fff",
    platforms: ["iOS", "Android"],
    downloads: "50K+",
    rating: 4.7,
    tech: ["Flutter", "Firebase", "Dart"],
    storeLinks: {
      ios: "https://apps.apple.com",
      android: "https://play.google.com",
    },
  },
  {
    id: 4,
    title: "Drift Kings",
    category: "Racing Game",
    description: "Realistic drift racing with 30 cars, 15 tracks, and live multiplayer tournaments. Features advanced physics engine and dynamic weather.",
    icon: "🏎️",
    gradient: "linear-gradient(135deg, #141e30 0%, #243b55 100%)",
    accent: "#f5a623",
    platforms: ["iOS", "Android"],
    downloads: "3.2M+",
    rating: 4.9,
    tech: ["Unity", "Photon", "Firebase"],
  },
  {
    id: 5,
    title: "MindZen",
    category: "Health & Fitness",
    description: "Guided meditation and sleep tracking app with 200+ audio sessions, breathing exercises, and mood analytics. Subscription-based revenue model.",
    icon: "🧘",
    gradient: "linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)",
    accent: "#fff",
    platforms: ["iOS", "Android"],
    downloads: "920K+",
    rating: 4.8,
    tech: ["Flutter", "RevenueCat", "Firebase"],
  },
  {
    id: 6,
    title: "Word Storm",
    category: "Puzzle Game",
    description: "Addictive word puzzle game with 5,000+ levels, daily challenges, and social features. Monetized with rewarded ads and premium hints.",
    icon: "💬",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    accent: "#fff",
    platforms: ["iOS", "Android", "Web"],
    downloads: "1.8M+",
    rating: 4.5,
    tech: ["Unity", "Firebase", "AdMob"],
  },
  {
    id: 7,
    title: "AR Measure+",
    category: "AR Experience",
    description: "Professional AR measuring tool using LiDAR and ARKit/ARCore. Accurate to 1mm — used by architects, interior designers, and engineers.",
    icon: "📐",
    gradient: "linear-gradient(135deg, #373b44 0%, #4286f4 100%)",
    accent: "#fff",
    platforms: ["iOS", "Android"],
    downloads: "410K+",
    rating: 4.7,
    tech: ["ARKit", "ARCore", "Swift", "Kotlin"],
  },
  {
    id: 8,
    title: "HypeCart",
    category: "E-commerce App",
    description: "Sneaker & streetwear marketplace with real-time drop alerts, size tracking, and instant checkout. Integrated Stripe and 3D product views.",
    icon: "👟",
    gradient: "linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)",
    accent: "#fff",
    platforms: ["iOS", "Android"],
    downloads: "280K+",
    rating: 4.6,
    tech: ["React Native", "Stripe", "Node.js"],
  },
];

const categories = ["All", "Action Game", "Hyper-casual", "Racing Game", "Puzzle Game", "Social App", "Health & Fitness", "AR Experience", "E-commerce App"];

const Portfolio = () => {
  const [projects, setProjects] = useState(defaultProjects);
  const [activeCategory, setActiveCategory] = useState("All");
  useScrollReveal();
  const [filteredProjects, setFilteredProjects] = useState(defaultProjects);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "home", "portfolio"),
      (snapshot) => {
        if (snapshot.exists() && snapshot.data().projects) {
          setProjects(snapshot.data().projects);
        } else {
          setProjects(defaultProjects);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (activeCategory === "All") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === activeCategory));
    }
  }, [activeCategory, projects]);

  return (
    <div className="portfolio-page">
      <SEO title="Portfolio" description="Explore our featured projects — mobile games, apps, and digital experiences shipped for iOS, Android, and web." path="/portfolio" />
      <section className="portfolio-hero">
        <div className="hero-bg-decoration" />
        <div className="hero-content">
          <span className="hero-badge">Our Work</span>
          <h1>Featured Projects</h1>
          <p>Showcasing our best work across gaming, apps, and digital experiences</p>
        </div>
      </section>

      <section className="filter-section reveal">
        <div className="category-filter">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      <section className="projects-grid-section reveal">
        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <div 
              className="project-card" 
              key={project.id}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="project-image" style={{ background: project.gradient }}>
                <div className="project-icon-display">
                  <span className="project-emoji">{project.icon}</span>
                  {project.tech && (
                    <div className="project-tech-pills">
                      {project.tech.slice(0, 2).map(t => (
                        <span key={t} className="tech-pill">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="project-overlay">
                  <Link to="/contact" className="view-project-btn">Start Similar Project</Link>
                </div>
              </div>
              <div className="project-info">
                <span className="project-category">{project.category}</span>
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-meta">
                  <div className="platforms">
                    {project.platforms.map((platform, idx) => (
                      <span key={idx} className="platform-tag">{platform}</span>
                    ))}
                  </div>
                  <div className="stats">
                    <span className="downloads">⬇ {project.downloads}</span>
                    <span className="rating">⭐ {project.rating}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="portfolio-stats reveal">
        <div className="stat-box">
          <h3>50+</h3>
          <p>Projects Delivered</p>
        </div>
        <div className="stat-box">
          <h3>15M+</h3>
          <p>Total Downloads</p>
        </div>
        <div className="stat-box">
          <h3>4.7</h3>
          <p>Average Rating</p>
        </div>
        <div className="stat-box">
          <h3>30+</h3>
          <p>Countries Reached</p>
        </div>
      </section>

      <section className="testimonials-section reveal">
        <h2>What Our Clients Say</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="quote-icon">"</div>
            <p>The team at MacroMatrix delivered beyond our expectations. Their attention to detail and technical expertise is unmatched.</p>
            <div className="testimonial-author">
              <div className="author-avatar">JD</div>
              <div>
                <strong>John Doe</strong>
                <span>CEO, GameStudio Inc</span>
              </div>
            </div>
          </div>
          <div className="testimonial-card">
            <div className="quote-icon">"</div>
            <p>Working with MacroMatrix was a game-changer for our business. They understood our vision and executed flawlessly.</p>
            <div className="testimonial-author">
              <div className="author-avatar">SM</div>
              <div>
                <strong>Sarah Miller</strong>
                <span>Founder, TechStart</span>
              </div>
            </div>
          </div>
          <div className="testimonial-card">
            <div className="quote-icon">"</div>
            <p>Their post-launch support and LiveOps management helped us achieve 3x user retention. Highly recommended!</p>
            <div className="testimonial-author">
              <div className="author-avatar">MK</div>
              <div>
                <strong>Michael Kim</strong>
                <span>CTO, MobileFirst</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="portfolio-cta reveal">
        <div className="cta-content">
          <h2>Want to See More?</h2>
          <p>Explore our full portfolio and discover how we can help bring your ideas to life.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Start a Project</Link>
            <Link to="/apps" className="btn-secondary">Browse Apps</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
